import { getExtraction, listExtractions } from "../../../api/knowledgeData/knowledgeDataApi";
import type { Extraction, ExtractionSummary } from "../../../api/knowledgeData/knowledgeDataDecoders";

export interface AcceptedChoice extends ExtractionSummary {
  readonly lineage: string;
  readonly defaultIncluded: boolean;
  readonly replaces: readonly string[];
}

type LineageDetail = Pick<Extraction, "source" | "lineage" | "superseded">;

/** Use the server's accepted head, including when acceptance order differs from creation. */
export function acceptedChoices(summaries: readonly ExtractionSummary[], details: readonly LineageDetail[]): AcceptedChoice[] {
  // Carried revisions retain their output-entry identity. Original annotations have
  // no indexed lineage until carried, so recover their head from the revision family.
  const family = (source: string) => source.replace(/\/r\d+$/, "");
  const heads = new Map(details.flatMap((detail) => detail.lineage.acceptedHead === null ? [] : [[family(detail.source), detail.lineage.acceptedHead] as const]));
  const headOf = (detail: LineageDetail) => detail.lineage.acceptedHead ?? heads.get(family(detail.source)) ?? null;
  return summaries.map((summary, index) => {
    const detail = details[index];
    const head = detail === undefined ? undefined : headOf(detail);
    return { ...summary, lineage: head ?? family(summary.source), defaultIncluded: head === null || head === summary.source,
      replaces: [...new Set([...(detail?.superseded ?? []), ...details.filter((other) => other.source !== summary.source && headOf(other) === summary.source).map((other) => other.source)])] };
  });
}

export async function loadAcceptedChoices(projectId: string, signal: AbortSignal): Promise<AcceptedChoice[]> {
  const summaries = await listExtractions(projectId, "accepted", signal);
  const details: Extraction[] = [];
  for (let index = 0; index < summaries.length; index += 8) {
    signal.throwIfAborted();
    details.push(...await Promise.all(summaries.slice(index, index + 8).map((summary) => getExtraction(summary.source, signal))));
  }
  return acceptedChoices(summaries, details);
}

/** The results a release holds: each one's default unless the user changed it. */
export function includedMembers(choices: readonly AcceptedChoice[], overrides: ReadonlyMap<string, boolean>): string[] {
  return choices.filter((choice) => overrides.get(choice.source) ?? choice.defaultIncluded).map((choice) => choice.source);
}

/** Include or leave out one result. Including a revision leaves out the other revisions of its
 * lineage: a release holds at most one (ADR-0025 §4). */
export function withInclusion(
  choices: readonly AcceptedChoice[],
  overrides: ReadonlyMap<string, boolean>,
  choice: AcceptedChoice,
  included: boolean,
): ReadonlyMap<string, boolean> {
  const next = new Map(overrides);
  if (included) {
    for (const other of choices) {
      if (other.lineage === choice.lineage) next.set(other.source, false);
    }
  }
  return next.set(choice.source, included);
}
